<script setup lang="ts">
/**
 * VideoPlayer Component
 * 
 * Renders a video player. 
 * Automatically detects if the source is a direct file (mp4, webm) or an embed URL.
 */

interface Props {
  src: string
  poster?: string // Optional poster image for HTML5 video
  autoplay?: boolean
  controls?: boolean
  muted?: boolean
  loop?: boolean
  className?: string
}

const props = withDefaults(defineProps<Props>(), {
  poster: '',
  autoplay: false,
  controls: true,
  muted: false,
  loop: false,
  className: ''
})

const isDirectFile = computed(() => {
  if (!props.src) return false
  const ext = props.src.split('.').pop()?.toLowerCase()
  return ['mp4', 'webm', 'ogg', 'mov'].includes(ext?.split('?')[0] || '')
})

// Helper to ensure embed URLs are correct (generic handling)
const embedUrl = computed(() => {
  if (isDirectFile.value) return ''
  
  // Basic handling: if it's already an embed URL, return it.
  // We could add logic here to convert youtube.com/watch?v= to youtube.com/embed/ if needed.
  // For now, assume the provided URL is embed-ready as per current usage in projects.
  return props.src
})

</script>

<template>
  <div class="relative w-full aspect-video bg-black rounded-xl overflow-hidden shadow-2xl border border-white/10" :class="className">
    
    <!-- Direct File (HTML5) -->
    <video
      v-if="isDirectFile"
      :src="src"
      :poster="poster"
      :autoplay="autoplay"
      :controls="controls"
      :muted="muted"
      :loop="loop"
      class="w-full h-full object-cover"
      playsinline
    >
      Votre navigateur ne supporte pas la lecture de vidéo.
    </video>

    <!-- Embed Iframe -->
    <iframe 
      v-else
      :src="embedUrl" 
      class="w-full h-full" 
      frameborder="0" 
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
      allowfullscreen
    ></iframe>

  </div>
</template>
